/**
 * UrlFieldRenderer
 *
 * Renders an `input-url` field with Copy / Open / Check buttons.
 * The Check button runs an immediate link-health check against the API
 * and displays the result inline (no DB write).
 *
 * If the field is already known to be dead from a prior scan, that
 * status is fetched on mount and shown until the user re-checks.
 */

import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Copy,
    ExternalLink,
    ShieldCheck,
    Loader2,
    CheckCircle2,
    XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
    checkUrl,
    getDeadLinkStatus,
    type LinkCheckResult,
    type LinkStatus,
} from '@/api/link-check';

interface Props {
    fieldName: string;
    label: string;
    value: string;
    placeholder?: string;
    helperText?: string | null;
    isRequired?: boolean;
    isReadonly?: boolean;
    widthClass?: string;
    onChange: (value: string) => void;
    /** Used to fetch persisted dead-link state for already-saved items */
    collection?: string;
    itemId?: string;
}

function isParseable(url: string): boolean {
    if (!url.trim()) return true;
    try {
        new URL(url.trim());
        return true;
    } catch {
        return false;
    }
}

export function UrlFieldRenderer({
    fieldName,
    label,
    value,
    placeholder,
    helperText,
    isRequired,
    isReadonly,
    widthClass,
    onChange,
    collection,
    itemId,
}: Props) {
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState<LinkCheckResult | null>(null);
    const [storedDead, setStoredDead] = useState<{ status: LinkStatus; code: number | null } | null>(null);

    const validFormat = isParseable(value);

    // On mount (or when item id changes), fetch any persisted dead-link state.
    useEffect(() => {
        let cancelled = false;
        if (!collection || !itemId) return;
        getDeadLinkStatus(collection, itemId, fieldName)
            .then((rec) => {
                if (cancelled) return;
                if (rec) {
                    setStoredDead({ status: rec.status, code: rec.status_code });
                } else {
                    setStoredDead(null);
                }
            })
            .catch(() => {
                /* non-blocking */
            });
        return () => {
            cancelled = true;
        };
    }, [collection, itemId, fieldName]);

    const handleCopy = async () => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            toast.success('URL copied to clipboard');
        } catch {
            toast.error('Failed to copy URL');
        }
    };

    const handleOpen = () => {
        if (!value) return;
        window.open(value.trim(), '_blank', 'noopener,noreferrer');
    };

    const handleCheck = async () => {
        if (!value.trim()) {
            toast.info('Enter a URL first');
            return;
        }
        setChecking(true);
        try {
            const r = await checkUrl(value.trim());
            setResult(r);
            // If the live check disagrees with the stored state, refresh the cache.
            if (r.status === 'alive' && storedDead) setStoredDead(null);
        } catch (err: any) {
            toast.error(err?.message ?? 'Check failed');
        } finally {
            setChecking(false);
        }
    };

    // Decide which status to render. Live check result wins; otherwise stored dead state.
    const displayStatus: { status: LinkStatus; code?: number | null } | null = result
        ? { status: result.status, code: result.statusCode ?? null }
        : storedDead;

    return (
        <div key={fieldName} className={cn('space-y-2', widthClass)}>
            <Label htmlFor={fieldName} className="capitalize">
                {label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div className="relative">
                <Input
                    id={fieldName}
                    type="text"
                    value={value}
                    onChange={(e) => {
                        if (isReadonly) return;
                        onChange(e.target.value);
                        // Reset cached check result when the URL changes
                        if (result) setResult(null);
                    }}
                    disabled={isReadonly}
                    placeholder={placeholder ?? 'https://example.com'}
                    className={cn(
                        'pr-28',
                        !validFormat && value && 'border-red-500 focus-visible:ring-red-500',
                    )}
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={handleCopy}
                        disabled={!value}
                        title="Copy URL"
                    >
                        <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={handleOpen}
                        disabled={!value || !validFormat}
                        title="Open URL"
                    >
                        <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={handleCheck}
                        disabled={!value || checking}
                        title="Check link"
                    >
                        {checking ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <ShieldCheck className="h-4 w-4" />
                        )}
                    </Button>
                </div>
            </div>
            {!validFormat && value && (
                <p className="text-xs text-red-600">
                    Please enter a valid URL (e.g., https://example.com)
                </p>
            )}
            {displayStatus && <StatusIndicator status={displayStatus.status} code={displayStatus.code ?? null} />}
            {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
        </div>
    );
}

function StatusIndicator({ status, code }: { status: LinkStatus; code: number | null }) {
    const isAlive = status === 'alive';
    const Icon = isAlive ? CheckCircle2 : XCircle;
    const palette: Record<LinkStatus, string> = {
        alive: 'bg-green-100 text-green-800 border-green-200',
        dead: 'bg-red-100 text-red-800 border-red-200',
        server_error: 'bg-red-100 text-red-800 border-red-200',
        timeout: 'bg-orange-100 text-orange-800 border-orange-200',
        dns_error: 'bg-orange-100 text-orange-800 border-orange-200',
        connection_error: 'bg-orange-100 text-orange-800 border-orange-200',
        ssl_error: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        redirect_loop: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        invalid_url: 'bg-gray-100 text-gray-700 border-gray-200',
        error: 'bg-gray-100 text-gray-700 border-gray-200',
    };

    return (
        <div className="flex items-center gap-2 text-xs">
            <Icon className={cn('h-3.5 w-3.5', isAlive ? 'text-green-600' : 'text-destructive')} />
            <Badge variant="outline" className={palette[status]}>
                {status}
                {code != null && ` (${code})`}
            </Badge>
        </div>
    );
}
